import type {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback } from "react";

import { BlogBreadcrumbs } from "@/components/blog/BlogBreadcrumbs";
import { BlogCta } from "@/components/blog/BlogCta";
import { BlogPostContent } from "@/components/blog/BlogPostContent";
import { BlogSeo, blogPostBreadcrumbs } from "@/components/blog/BlogSeo";
import { PostMeta } from "@/components/blog/PostMeta";
import { RelatedPosts } from "@/components/blog/RelatedPosts";
import Footer from "@/components/shared/Footer";
import HeroSection from "@/components/shared/HeroSection";
import Spinner from "@/components/shared/Spinner";
import { topicPath } from "@/lib/blog/paths";
import { topicForPost, topicForTag, type BlogTopic } from "@/lib/blog/topics";
import { useClientFallback } from "@/lib/blog/useClientFallback";
import {
  getPost,
  getPostPage,
  getRelatedPosts,
  type BlogPost,
  type BlogPostSummary,
} from "@/lib/ghost";

const PRERENDERED_PAGES = [1, 2];

export const getStaticPaths = (async () => {
  const pages = await Promise.all(PRERENDERED_PAGES.map((page) => getPostPage(page)));
  const slugs = pages.flatMap((result) => result?.posts.map((post) => post.slug) ?? []);

  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: "blocking",
  };
}) satisfies GetStaticPaths;

export const getStaticProps = (async (context: GetStaticPropsContext) => {
  const slug = context.params?.slug;
  if (typeof slug !== "string") return { notFound: true };

  const post = await getPost(slug);
  if (!post) return { notFound: true };

  const related = await getRelatedPosts(post);

  return { props: { post, related }, revalidate: 86400 };
}) satisfies GetStaticProps<{ post: BlogPost; related: BlogPostSummary[] }>;

const hubsForPost = (post: BlogPost): BlogTopic[] => {
  const seen = new Set<string>();
  const hubs: BlogTopic[] = [];

  for (const tag of post.tags) {
    const topic = topicForTag(tag.slug);
    if (!topic || seen.has(topic.slug)) continue;
    seen.add(topic.slug);
    hubs.push(topic);
  }

  return hubs;
};

export default function BlogPostPage({
  post: initialPost,
  related: initialRelated,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter();
  const slug = typeof router.query.slug === "string" ? router.query.slug : "";

  const fetchPost = useCallback(() => getPost(slug), [slug]);
  const { data: post, loading } = useClientFallback<BlogPost>(initialPost, slug, fetchPost);

  const fetchRelated = useCallback(
    () => (post ? getRelatedPosts(post) : Promise.resolve(null)),
    [post],
  );
  const { data: related } = useClientFallback<BlogPostSummary[]>(
    initialRelated,
    post ? slug : "",
    fetchRelated,
  );

  if (!post) {
    return (
      <>
        {loading ? (
          <Spinner />
        ) : (
          <div className="px-5 py-24 text-center">
            <p className="text-2xl font-semibold text-gray-900">This post could not be loaded.</p>
            <Link href="/blog" className="mt-4 inline-block text-lg text-crayoBlue hover:underline">
              Back to blog
            </Link>
          </div>
        )}
        <Footer />
      </>
    );
  }

  const topic = topicForPost(post);
  const hubs = hubsForPost(post);

  return (
    <>
      <BlogSeo post={post} />
      <HeroSection
        wrapperStyle="py-12 lg:py-16"
        topComponent={
          <div className="flex w-full flex-col items-center gap-4">
            <BlogBreadcrumbs items={blogPostBreadcrumbs(post)} />
            {topic && (
              <Link
                href={topicPath(topic.slug)}
                className="rounded-full border border-gray-200 bg-gray-100 px-3.5 py-1 text-sm text-gray-900 transition-colors hover:border-gray-300 hover:bg-gray-200"
              >
                {topic.name}
              </Link>
            )}
          </div>
        }
        heading={
          <h1 className="mx-auto max-w-2xl text-center text-3xl font-semibold text-gray-900 sm:text-4xl lg:text-balance lg:tracking-[0.015em]">
            {post.title}
          </h1>
        }
        body={<PostMeta post={post} />}
      />
      <BlogPostContent post={post} />
      <div className="mx-auto w-full max-w-screen-lg space-y-12 px-5 pb-16 md:pb-24">
        {hubs.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {hubs.map((hub) => (
              <Link
                key={hub.slug}
                href={topicPath(hub.slug)}
                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900"
              >
                {hub.name}
              </Link>
            ))}
          </div>
        )}
        <BlogCta topic={topic} />
        <RelatedPosts posts={related ?? []} />
      </div>
      <Footer />
    </>
  );
}
